/**
 * HTML 清理工具函数
 * 用于清理编辑器生成的HTML，移除编辑器特定属性，确保邮件客户端兼容性
 */

import { getEnglishTerm } from "../constants/contentBlockConstants";
import contentBlockRenderer from "../renderers/ContentBlockRenderer";
import { getEffectiveData } from "./dataScopeResolver";

/**
 * 清理HTML中的编辑器特定属性，并为内容块添加邮件客户端样式
 * @param {string} html - 原始HTML内容
 * @returns {string} - 清理后的HTML内容
 */
export function cleanHtmlForEmail(html) {
    if (!html) return "";

    // 创建临时DOM元素来处理HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // 需要移除的编辑器特定属性
    const editorAttributes = ["data-content-block-type", "data-content-block-id", "data-content-block-label", "data-instance-id", "data-filter-revision", "data-status", "data-is-inline", "data-node-view-wrapper", "data-drag-handle", "contenteditable", "spellcheck", "data-pm-slice", "data-pm-context", "data-tiptap-node", "data-type", "data-uploading"];

    // 需要移除的编辑器特定类名
    const editorClasses = ["content-block-node", "inline-content-block-node", "content-block-wrapper", "content-block-header", "content-block-content", "content-block-drag-area", "content-block-instance-index", "highlighted", "ProseMirror", "ProseMirror-focused"];

    // 递归清理所有元素
    function cleanElement(element) {
        // 检查是否是内容块元素（在清理属性之前）
        const isContentBlock = element.hasAttribute("data-content-block-type");
        const isInlineContentBlock = element.hasAttribute("data-is-inline") && element.getAttribute("data-is-inline") === "true";
        const contentBlockType = element.getAttribute("data-content-block-type");

        // 移除编辑器特定属性
        editorAttributes.forEach((attr) => {
            element.removeAttribute(attr);
        });

        // 清理class属性
        if (element.className) {
            const classList = Array.from(element.classList);
            const cleanedClasses = classList.filter((cls) => !editorClasses.some((editorCls) => cls.includes(editorCls)));

            if (cleanedClasses.length > 0) {
                element.className = cleanedClasses.join(" ");
            } else {
                element.removeAttribute("class");
            }
        }

        // 为内容块添加邮件客户端兼容样式
        if (isContentBlock) {
            if (isInlineContentBlock) {
                // 行内内容块样式 - 加粗+下划线显示
                element.style.fontWeight = "bold";
                element.style.textDecoration = "underline";
                element.style.color = "#1890ff";
                element.style.backgroundColor = "#f0f8ff";
                element.style.padding = "2px 4px";
                element.style.borderRadius = "3px";
                element.style.border = "1px solid #d6e4ff";
                element.style.display = "inline";
            } else {
                // 块级内容块样式
                element.style.border = "1px solid #d9d9d9";
                element.style.borderRadius = "6px";
                element.style.margin = "10px 0";
                element.style.backgroundColor = "#ffffff";

                // 为表格内容块添加特殊样式
                if (contentBlockType && (contentBlockType.includes("List") || contentBlockType.includes("Table"))) {
                    const tables = element.querySelectorAll("table");
                    tables.forEach((table) => {
                        // 确保表格样式在邮件客户端中正确显示
                        table.style.width = "100%";
                        table.style.borderCollapse = "collapse";
                        table.style.fontSize = "12px";
                        table.style.fontFamily = "Arial, sans-serif";

                        // 为表头添加样式
                        const headers = table.querySelectorAll("th");
                        headers.forEach((th) => {
                            th.style.backgroundColor = "#f5f5f5";
                            th.style.border = "1px solid #d9d9d9";
                            th.style.padding = "8px 6px";
                            th.style.fontSize = "11px";
                            th.style.fontWeight = "bold";
                            th.style.textAlign = "center";
                        });

                        // 为表格单元格添加样式
                        const cells = table.querySelectorAll("td");
                        cells.forEach((td) => {
                            td.style.border = "1px solid #e8e8e8";
                            td.style.padding = "4px 6px";
                            td.style.fontSize = "11px";

                            // 为状态单元格添加颜色
                            const cellText = td.textContent || "";
                            if (cellText === "新增" || cellText === getEnglishTerm("新增")) {
                                td.style.backgroundColor = "#f6ffed";
                                td.style.color = "#52c41a";
                            } else if (cellText === "变更" || cellText === getEnglishTerm("变更")) {
                                td.style.backgroundColor = "#fff7e6";
                                td.style.color = "#fa8c16";
                            } else if (cellText === "删除" || cellText === getEnglishTerm("删除")) {
                                td.style.backgroundColor = "#fff1f0";
                                td.style.color = "#ff4d4f";
                                td.style.textDecoration = "line-through";
                            }
                        });
                    });
                }
            }
        }

        // 为图片元素添加邮件客户端兼容样式
        // 保留 data-object-key 属性，供后端 CID 内嵌处理
        if (element.tagName.toLowerCase() === "img") {
            element.style.maxWidth = "100%";
            element.style.height = "auto";
            element.style.display = "block";
            element.style.margin = "8px 0";
            // 移除 blob URL (上传中的占位图片)
            if (element.src && element.src.startsWith("blob:")) {
                element.remove();
                return;
            }
        }

        // 为列表元素添加邮件客户端兼容样式
        if (element.tagName.toLowerCase() === "ul") {
            // 无序列表样式
            element.style.margin = "16px 0";
            element.style.paddingLeft = "20px";
            element.style.listStyleType = "disc";
            element.style.listStylePosition = "outside";
        } else if (element.tagName.toLowerCase() === "ol") {
            // 有序列表样式
            element.style.margin = "16px 0";
            element.style.paddingLeft = "20px";
            element.style.listStyleType = "decimal";
            element.style.listStylePosition = "outside";
        } else if (element.tagName.toLowerCase() === "li") {
            // 列表项样式
            element.style.margin = "4px 0";
            element.style.lineHeight = "1.5";
            element.style.display = "list-item";
        }

        // 清理style属性中的编辑器特定样式
        if (element.style) {
            // 移除可能影响邮件显示的编辑器样式
            element.style.removeProperty("outline");
            element.style.removeProperty("user-select");
            element.style.removeProperty("cursor");
            element.style.removeProperty("position");
            element.style.removeProperty("z-index");

            // 如果style为空，移除style属性
            if (!element.style.cssText) {
                element.removeAttribute("style");
            }
        }

        // 递归处理子元素
        Array.from(element.children).forEach((child) => {
            cleanElement(child);
        });
    }

    // 处理所有元素
    Array.from(tempDiv.children).forEach((element) => {
        cleanElement(element);
    });

    // 额外处理：移除空的内容块容器
    const emptyContentBlocks = tempDiv.querySelectorAll('[data-content-block-type=""], .content-block-node:empty, .inline-content-block-node:empty');
    emptyContentBlocks.forEach((element) => {
        element.remove();
    });

    return tempDiv.innerHTML;
}

/**
 * 为预览生成HTML（保留编辑器属性用于交互，但添加预览样式）
 * @param {string} html - 原始HTML内容
 * @returns {string} - 用于预览的HTML内容
 */
export function prepareHtmlForPreview(html) {
    if (!html) return "";

    // 预览模式下保留大部分属性，只做基本清理
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // 只移除一些可能影响预览显示的属性
    const attributesToRemove = ["contenteditable", "spellcheck", "data-pm-slice", "data-pm-context"];

    function cleanForPreview(element) {
        // 检查是否是内容块元素
        const isContentBlock = element.hasAttribute("data-content-block-type");
        const isInlineContentBlock = element.hasAttribute("data-is-inline") && element.getAttribute("data-is-inline") === "true";
        const contentBlockType = element.getAttribute("data-content-block-type");

        attributesToRemove.forEach((attr) => {
            element.removeAttribute(attr);
        });

        // 为内容块添加预览样式
        if (isContentBlock) {
            if (isInlineContentBlock) {
                // 行内内容块预览样式 - 加粗+下划线显示
                element.style.fontWeight = "bold";
                element.style.textDecoration = "underline";
                element.style.color = "#1890ff";
                element.style.backgroundColor = "#f0f8ff";
                element.style.padding = "2px 4px";
                element.style.borderRadius = "3px";
                element.style.border = "1px solid #d6e4ff";
                element.style.display = "inline";
            } else {
                // 块级内容块预览样式
                element.style.border = "1px solid #d9d9d9";
                element.style.borderRadius = "6px";
                element.style.margin = "10px 0";
                element.style.backgroundColor = "#ffffff";

                // 为表格内容块添加预览样式
                if (contentBlockType && (contentBlockType.includes("List") || contentBlockType.includes("Table"))) {
                    const tables = element.querySelectorAll("table");
                    tables.forEach((table) => {
                        // 确保表格在预览中正确显示
                        table.style.width = "100%";
                        table.style.borderCollapse = "collapse";
                        table.style.fontSize = "12px";
                        table.style.fontFamily = "Arial, sans-serif";

                        // 为表头添加样式
                        const headers = table.querySelectorAll("th");
                        headers.forEach((th) => {
                            th.style.backgroundColor = "#f5f5f5";
                            th.style.border = "1px solid #d9d9d9";
                            th.style.padding = "8px 6px";
                            th.style.fontSize = "11px";
                            th.style.fontWeight = "bold";
                            th.style.textAlign = "center";
                        });

                        // 为表格单元格添加样式
                        const cells = table.querySelectorAll("td");
                        cells.forEach((td) => {
                            td.style.border = "1px solid #e8e8e8";
                            td.style.padding = "4px 6px";
                            td.style.fontSize = "11px";

                            // 为状态单元格添加颜色
                            const cellText = td.textContent || "";
                            if (cellText === "新增") {
                                td.style.backgroundColor = "#f6ffed";
                                td.style.color = "#52c41a";
                            } else if (cellText === "变更") {
                                td.style.backgroundColor = "#fff7e6";
                                td.style.color = "#fa8c16";
                            } else if (cellText === "删除") {
                                td.style.backgroundColor = "#fff1f0";
                                td.style.color = "#ff4d4f";
                                td.style.textDecoration = "line-through";
                            }
                        });
                    });
                }
            }
        }

        // 为列表元素添加预览样式（与邮件样式保持一致）
        if (element.tagName.toLowerCase() === "ul") {
            // 无序列表样式
            element.style.margin = "16px 0";
            element.style.paddingLeft = "20px";
            element.style.listStyleType = "disc";
            element.style.listStylePosition = "outside";
        } else if (element.tagName.toLowerCase() === "ol") {
            // 有序列表样式
            element.style.margin = "16px 0";
            element.style.paddingLeft = "20px";
            element.style.listStyleType = "decimal";
            element.style.listStylePosition = "outside";
        } else if (element.tagName.toLowerCase() === "li") {
            // 列表项样式
            element.style.margin = "4px 0";
            element.style.lineHeight = "1.5";
            element.style.display = "list-item";
        }

        // 移除可能影响预览的样式
        if (element.style) {
            element.style.removeProperty("outline");
            element.style.removeProperty("cursor");
        }

        // 递归处理子元素
        Array.from(element.children).forEach((child) => {
            cleanForPreview(child);
        });
    }

    Array.from(tempDiv.children).forEach((element) => {
        cleanForPreview(element);
    });

    return tempDiv.innerHTML;
}

/**
 * 验证HTML是否包含内容块
 * @param {string} html - HTML内容
 * @returns {boolean} - 是否包含内容块
 */
export function hasContentBlocks(html) {
    if (!html) return false;

    return html.includes("data-content-block-type") || html.includes("content-block-node") || html.includes("inline-content-block-node");
}

/**
 * 获取HTML中的内容块信息
 * @param {string} html - HTML内容
 * @returns {Array} - 内容块信息数组
 */
export function extractContentBlockInfo(html) {
    if (!html) return [];

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    const contentBlocks = [];

    // 查找所有内容块元素
    const blockElements = tempDiv.querySelectorAll("[data-content-block-type]");

    blockElements.forEach((element, index) => {
        contentBlocks.push({
            index: index + 1,
            type: element.getAttribute("data-content-block-type"),
            id: element.getAttribute("data-content-block-id"),
            instanceId: element.getAttribute("data-instance-id"),
            label: element.getAttribute("data-content-block-label"),
            isInline: element.getAttribute("data-is-inline") === "true",
            content: element.textContent || element.innerText || "",
        });
    });

    return contentBlocks;
}

/**
 * 将内容块渲染为实际的HTML内容
 * @param {string} html - 包含内容块的HTML
 * @param {object} businessContext - 业务上下文数据
 * @returns {string} - 渲染后的HTML内容
 */
export function renderContentBlocksToHtml(html, businessContext = {}, contentBlockInstances = []) {
    if (!html) return "";

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // 查找所有内容块元素
    const contentBlocks = tempDiv.querySelectorAll("[data-content-block-type]");

    contentBlocks.forEach((element, index) => {
        const contentBlockType = element.getAttribute("data-content-block-type");
        const contentBlockId = element.getAttribute("data-content-block-id");
        const instanceId = element.getAttribute("data-instance-id");
        const contentBlockInstance = Array.isArray(contentBlockInstances) ? contentBlockInstances.find((instance) => instance.instanceId === instanceId) : null;

        // 根本原因修复：
        // 预览时，HTML来自于 editor.getHTML()，它已经将节点渲染为特定的HTML结构。
        // 行内节点会被渲染成一个带 `inlineContentBlock-node` 类的 `<span>` 标签。
        // 需要检查多种可能的属性和类名格式。
        const hasInlineAttribute = element.getAttribute("data-is-inline") === "true" || element.getAttribute("isinline") === "true";
        const isInlineElement = element.tagName.toLowerCase() === "span" && (element.classList.contains("inlineContentBlock-node") || element.classList.contains("node-inlineContentBlock"));
        const isInline = hasInlineAttribute || isInlineElement;

        // 获取业务数据
        const businessData = getBusinessDataForContentBlock(contentBlockType, contentBlockId, businessContext);

        if (businessData) {
            const effectiveBusinessData = getEffectiveData(contentBlockInstance, businessData);

            if (isInline) {
                // 行内内容块：使用统一渲染服务，传递内容块ID用于区分不同用途
                const options = { ...businessContext, contentBlockId, contentBlockInstance };
                const htmlContent = contentBlockRenderer.renderInlineText(contentBlockType, effectiveBusinessData, options);
                const tempSpan = document.createElement("span");
                tempSpan.innerHTML = htmlContent;
                // 将渲染后的第一个子元素（例如 <strong>）替换原始占位符
                if (tempSpan.firstElementChild) {
                    element.replaceWith(tempSpan.firstElementChild);
                } else {
                    // 如果没有子元素（纯文本），则直接替换
                    element.replaceWith(tempSpan);
                }
            } else {
                // 块级内容块：使用统一渲染服务
                const htmlContent = contentBlockRenderer.renderForPreview(contentBlockType, effectiveBusinessData, {
                    ...businessContext,
                    contentBlockInstance,
                });
                const containerDiv = document.createElement("div");
                containerDiv.style.margin = "10px 0";
                containerDiv.style.backgroundColor = "#ffffff";
                containerDiv.innerHTML = htmlContent;
                element.replaceWith(containerDiv);
            }
        } else {
            // 没有业务数据时，显示占位符
            if (isInline) {
                const placeholderSpan = document.createElement("span");
                placeholderSpan.style.fontStyle = "italic";
                placeholderSpan.style.color = "#999";
                placeholderSpan.textContent = `[${element.getAttribute("data-content-block-label") || contentBlockType}]`;
                element.replaceWith(placeholderSpan);
            } else {
                const placeholderDiv = document.createElement("div");
                placeholderDiv.style.padding = "12px";
                placeholderDiv.style.textAlign = "center";
                placeholderDiv.style.color = "#999";
                placeholderDiv.style.fontStyle = "italic";
                placeholderDiv.style.border = "1px dashed #d9d9d9";
                placeholderDiv.style.borderRadius = "6px";
                placeholderDiv.style.margin = "10px 0";
                placeholderDiv.textContent = `[${element.getAttribute("data-content-block-label") || contentBlockType}]`;
                element.replaceWith(placeholderDiv);
            }
        }
    });

    return tempDiv.innerHTML;
}

/**
 * 获取内容块的业务数据
 * 
 * 查找策略（按优先级）：
 * 1. 按 id 精确匹配 contentBlocks
 * 2. 按 type 匹配 contentBlocks
 * 3. 处理动态扩展的 ID 格式（originalId_index）
 * 4. 从顶层数据兜底（兼容简化的数据结构）
 */
function getBusinessDataForContentBlock(contentBlockType, contentBlockId, businessContext) {
    // 策略1-3: 尝试从 contentBlocks 数组获取
    if (businessContext.contentBlocks && businessContext.contentBlocks.length > 0) {
        // 策略1: 按 id 精确匹配
        let block = businessContext.contentBlocks.find((b) => b.id === contentBlockId);

        // 策略2: 按 type 匹配
        if (!block) {
            block = businessContext.contentBlocks.find((b) => b.type === contentBlockType);
        }

        // 策略3: 处理动态扩展的ID格式（originalId_index）
        if (!block && contentBlockId && contentBlockId.includes("_")) {
            const originalId = contentBlockId.split("_")[0];
            block = businessContext.contentBlocks.find((b) => b.id === originalId || b.id === contentBlockId);
        }

        if (block && block.data) {
            console.log(`[getBusinessDataForContentBlock] 从 contentBlocks 找到数据:`, {
                contentBlockType,
                contentBlockId,
                matchedBlockId: block.id,
                matchedBlockType: block.type,
            });
            return block.data;
        }
    }

    // 策略4: 从顶层数据兜底（支持多种数据结构）
    console.log(`[getBusinessDataForContentBlock] contentBlocks 未匹配，尝试顶层数据兜底`, {
        contentBlockType,
        contentBlockId,
        availableTopLevelKeys: Object.keys(businessContext).filter(k => k !== 'contentBlocks'),
    });

    switch (contentBlockType) {
        // 计划航班相关
        case "scheduleInfo":
            return businessContext.schedule || businessContext.schedule_info;
            
        case "scheduleFlightList":
        case "scheduleFlightListWithComparison":
            if (businessContext.flights) return businessContext.flights;
            if (businessContext.comparisonData) return businessContext.comparisonData;
            if (businessContext.schedules) return { flights: businessContext.schedules };
            return null;
        
        // 执行航班相关
        case "operationFlightList":
            // 支持多种数据结构
            if (businessContext.flightOperations) {
                console.log(`[getBusinessDataForContentBlock] 使用顶层 flightOperations 兜底`);
                return businessContext.flightOperations;
            }
            if (businessContext.operations) {
                return businessContext.operations;
            }
            // 检查 rawData
            if (businessContext.rawData?.flights) {
                return businessContext.rawData.flights;
            }
            // 检查 dataPayload（GenericEmailSendDrawer 传递的原始数据）
            if (businessContext.dataPayload && Array.isArray(businessContext.dataPayload)) {
                console.log(`[getBusinessDataForContentBlock] 使用顶层 dataPayload 兜底`);
                return businessContext.dataPayload;
            }
            return null;
        
        // 前序航班相关
        case "flightWithPredecessor":
            if (businessContext.flightsWithPredecessor) return businessContext.flightsWithPredecessor;
            if (businessContext.flightOperations) return businessContext.flightOperations;
            if (businessContext.rawData?.flights) return businessContext.rawData.flights;
            if (businessContext.dataPayload) return businessContext.dataPayload;
            return null;
            
        default:
            console.warn(`[getBusinessDataForContentBlock] 未支持的内容块类型:`, {
                contentBlockType,
                contentBlockId,
                availableBlocks: businessContext.contentBlocks?.map((b) => ({ id: b.id, type: b.type })) || [],
                availableTopLevelKeys: Object.keys(businessContext).filter(k => k !== 'contentBlocks'),
            });
            return null;
    }
}

/**
 * 从 HTML 中提取需要 CID 内嵌的图片信息
 * 在邮件提交时调用，收集所有通过编辑器上传的图片的 OSS object_key
 * 
 * @param {string} html - 编辑器生成的 HTML 内容
 * @returns {Array<{object_key: string, src: string}>} - 内嵌图片信息列表
 */
export function extractInlineImages(html) {
    if (!html) return [];

    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    const images = [];
    const imgElements = tempDiv.querySelectorAll("img[data-object-key]");

    imgElements.forEach((img) => {
        const objectKey = img.getAttribute("data-object-key");
        if (objectKey) {
            images.push({
                object_key: objectKey,
                src: img.getAttribute("src") || "",
                alt: img.getAttribute("alt") || "",
            });
        }
    });

    return images;
}
